// --------------------- Import React ---------------------
import { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

// --------------------- Import Hooks ---------------------
import { useCotaUsuario } from "../hooks/useCotaUsuario";
import { useAtualizarDadosAnuncioPropriedade } from "../hooks/useAtualizarDadosAnuncio";
import { useError } from "../../../uteis/mensagens/useError";
import { useSuccess } from "../../../uteis/mensagens/useSuccess";

// --------------------- Import Styles ---------------------
import {
  ContainerModal,
  TitleModal,
  formStyle,
  ContainerModalBtn,
  btnStyle,
  TextModal,
} from "../styles";

// --------------------- Import Antd-Design ---------------------
import {
  Modal,
  Form,
  Select,
  InputNumber,
  Button,
  Skeleton,
  Input,
} from "antd";
// --------------------- Import Interfaces ---------------------
import { IModal } from "../interfaces/Modal";
import { CotaUsuario } from "../interfaces/CotaUsuario";
import { AnuncioPropriedade } from "../interfaces/AnuncioPropriedade";
// --------------------- Import Constants ---------------------
import { situacaoPropriedade } from "../constant/situacaoPropriedade";
import { layout } from "../constant/layout";

export default function ModalAnuncioPropriedade({
  isModalOpen,
  handleFechar,
  cotaUsuarioId,
}: IModal) {
  const [formAnuncioPropriedade] = Form.useForm();

  // Exibe a Mensagem de  Erro
  const { showError, contextHolder: errorContextHolder } = useError();

  // Exibe a Mensagem de  Sucesso
  const { showSuccess, contextHolder: successContextHolder } = useSuccess();

  // Estado para controlar se o erro já foi exibido
  const [errorDisplayed, setErrorDisplayed] = useState(false);

  // Estado para controlar se o sucesso já foi exibido
  const [successDisplayed, setSuccessDisplayed] = useState(false);

  // Hook de navegação do React Router
  const navigate = useNavigate();

  // Use para buscar as info de Cota Usuário
  const { isLoading: isLoadingCotaUsuario, cotaUsuario } = useCotaUsuario(
    cotaUsuarioId,
    isModalOpen
  );

  // Estado inicial formulário de Validação Proprietario Atualizar Usuario
  const [anuncioPropriedadeMaisInfo, setAnuncioPropriedadeMaisInfo] =
    useState<AnuncioPropriedade>({});

  // UseState necessário para armazenado do formulario MaisInfo
  const [atualizarMaisInfo, setAtualizarMaisInfo] = useState<CotaUsuario>({});

  const situacaoRef = useRef<string>();

  const maisInfoChange = (changedValues: Partial<any>, allValues: any) => {
    const { situacao } = allValues;

    situacaoRef.current = situacao;

    // Atualize o estado com os valores modificados
    setAnuncioPropriedadeMaisInfo((prevState) => ({
      ...prevState,
      ...changedValues,
    }));

    if (situacao !== "NAO_QUITADO") {
      formAnuncioPropriedade.setFieldsValue({ saldoDevedor: null });
    }

    setAtualizarMaisInfo(allValues);
  };

  const {
    atualizarDadosAnuncioPropriedade,

    error,
    success,
  } = useAtualizarDadosAnuncioPropriedade();

  const errorRef = useRef<string | null>("");

  // Atualiza o valor de errorRef  toda vez que o error e warning mudar
  useEffect(() => {
    if (error) {
      errorRef.current = error;
    }
  }, [error]);

  useEffect(() => {
    if (isModalOpen) {
      formAnuncioPropriedade.setFieldsValue({
        situacao: cotaUsuario?.situacao,
        valorVenda: cotaUsuario?.valorVenda,
        valorCondominio: cotaUsuario?.valorCondominio,
        saldoDevedor: cotaUsuario?.saldoDevedor,
        descricao_anuncio: cotaUsuario?.descricao_anuncio,
      });
    }
  }, [isModalOpen, cotaUsuario, formAnuncioPropriedade]);

  // useEffect para lidar com o sucesso
  useEffect(() => {
    if (isModalOpen) {
      if (success && !successDisplayed) {
        // Verifica se o sucesso já foi exibido
        showSuccess(success);
        setSuccessDisplayed(true);
        setTimeout(() => {
          navigate(0); // Use navigate(0) para simular um refresh completo
        }, 3000);
      }
      if (errorRef.current && !errorDisplayed) {
        // Verifica se o erro já foi exibido
        showError(errorRef.current);
        setErrorDisplayed(true); // Marca que o erro foi exibido

        // Limpa estado de error após 3 segundos
        setTimeout(() => {
          setErrorDisplayed(false);
          errorRef.current = null;
        }, 3000);
      }
    }

    // Limpa o estado de sucesso quando o modal é fechado
    if (!isModalOpen) {
      setSuccessDisplayed(false);
      setErrorDisplayed(false);
    }
  }, [
    isModalOpen,
    success,
    showSuccess,
    successDisplayed,
    errorDisplayed,
    showError,
    navigate,
  ]);

  return (
    <Modal open={isModalOpen} onCancel={handleFechar} footer={null}>
      {isLoadingCotaUsuario && <Skeleton active />}

      {!isLoadingCotaUsuario && cotaUsuario && (
        <>
          <ContainerModal>
            <div key="error-message-holder">{errorContextHolder}</div>
            <div key="success-message-holder">{successContextHolder}</div>
            <TitleModal>Mais Info Anuncio Propriedade</TitleModal>
            <Form
              {...layout}
              form={formAnuncioPropriedade}
              name="formAnuncioPropriedade"
              onValuesChange={(changedValues, allValues) =>
                maisInfoChange(changedValues, allValues)
              }
              initialValues={anuncioPropriedadeMaisInfo}
              style={formStyle}
            >
              <TextModal>Mais Informações</TextModal>
              <Form.Item
                name={["situacao"]}
                label="Situação da propriedade"
                className="formItems"
                //style={{ width: "100%", marginBottom: "24px" }}
                labelCol={{ span: 10 }}
                wrapperCol={{ span: 10 }}
              >
                <Select placeholder="Situação da propriedade">
                  {situacaoPropriedade.map((dias, index) => (
                    <Select.Option key={dias.id} value={dias.value}>
                      {dias.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name={["valorVenda"]}
                label="Valor da Venda"
                className="formItems"
                style={{ width: "60%", marginBottom: "24px" }}
                labelCol={{ span: 11 }}
                wrapperCol={{ span: 24 }}
              >
                <InputNumber
                  placeholder="Digite o valor da venda"
                  formatter={(value) => {
                    if (value === undefined || value === null) return "";
                    return `R$ ${Number(value).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  }}
                  parser={(value) => {
                    if (!value) return "";
                    const onlyNumbers = value.replace(/\D+/g, ""); // Remove tudo que não for número
                    const parsedValue = (parseFloat(onlyNumbers) / 100).toFixed(
                      2
                    ); // Converte para decimal
                    return isNaN(Number(parsedValue)) ? "" : parsedValue;
                  }}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item
                name={["valorCondominio"]}
                label="Valor do Condomínio"
                className="formItems"
                style={{ width: "60%", marginBottom: "24px" }}
                labelCol={{ span: 14 }}
                wrapperCol={{ span: 50 }}
              >
                <InputNumber
                  placeholder="Digite o valor do condomínio"
                  formatter={(value) => {
                    if (value === undefined || value === null) return "";
                    return `R$ ${Number(value).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  }}
                  parser={(value) => {
                    if (!value) return "";
                    const onlyNumbers = value.replace(/\D+/g, ""); // Remove tudo que não for número
                    const parsedValue = (parseFloat(onlyNumbers) / 100).toFixed(
                      2
                    ); // Converte para decimal
                    return isNaN(Number(parsedValue)) ? "" : parsedValue;
                  }}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              {anuncioPropriedadeMaisInfo.situacao === "NAO_QUITADO" && (
                <Form.Item
                  name={["saldoDevedor"]}
                  label="Saldo devedor"
                  className="formItems"
                  style={{ width: "60%", marginBottom: "24px" }}
                  labelCol={{ span: 11 }}
                  wrapperCol={{ span: 24 }}
                >
                  <InputNumber
                    placeholder="Digite o Saldo devedor"
                    formatter={(value) => {
                      if (value === undefined || value === null) return "";
                      return `R$ ${Number(value).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`;
                    }}
                    parser={(value) => {
                      if (!value) return "";
                      const onlyNumbers = value.replace(/\D+/g, ""); // Remove tudo que não for número
                      const parsedValue = (
                        parseFloat(onlyNumbers) / 100
                      ).toFixed(2); // Converte para decimal
                      return isNaN(Number(parsedValue)) ? "" : parsedValue;
                    }}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
              <Form.Item
                name={["descricao_anuncio"]}
                label="Descrição"
                className="formItems"
                labelCol={{ span: 10 }}
                wrapperCol={{ span: 10 }}
              >
                <Input.TextArea
                  onChange={(e) => {
                    formAnuncioPropriedade.setFieldsValue({
                      descricao_anuncio: e.target.value.replace(/\n/g, "<br/>"),
                    });
                  }}
                />
              </Form.Item>
            </Form>
          </ContainerModal>
          <ContainerModalBtn>
            <Button
              type="primary"
              onClick={() =>
                atualizarDadosAnuncioPropriedade(
                  cotaUsuario?.id,
                  atualizarMaisInfo
                )
              }
              style={btnStyle}
              size="large"
            >
              Atualizar
            </Button>
          </ContainerModalBtn>
        </>
      )}
    </Modal>
  );
}
